// src/components/dashboard/DateRangeSelector.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { FaCalendarAlt, FaChevronDown } from 'react-icons/fa';

const SelectorContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  z-index: 10;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(2)};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.medium};
  min-width: 180px;
  justify-content: space-between;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-top: ${props => props.theme.spacing(1)};
  z-index: 100;
  min-width: 240px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MenuItem = styled.div`
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(2)};
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.colors.background};
  }
  
  &.active {
    background-color: ${props => props.theme.colors.primary}22;
    color: ${props => props.theme.colors.primary};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
  }
`;

const CustomRangeContainer = styled.div`
  padding: ${props => props.theme.spacing(2)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
`;

const InputGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing(1)};
  
  label {
    display: block;
    margin-bottom: ${props => props.theme.spacing(0.5)};
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
  }
  
  input {
    width: 100%;
    padding: ${props => props.theme.spacing(1)};
    border: 1px solid ${props => props.theme.colors.inputGray};
    border-radius: ${props => props.theme.borderRadius};
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing(1)};
  background-color: ${props => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  margin-top: ${props => props.theme.spacing(1)};
  
  &:hover {
    opacity: 0.9;
  }
`;

const DateRangeSelector = ({ period, customRange, onPeriodChange, onCustomRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(
    customRange.startDate.toISOString().split('T')[0]
  );
  const [tempEndDate, setTempEndDate] = useState(
    customRange.endDate.toISOString().split('T')[0]
  );

  const getPeriodLabel = () => {
    switch (period) {
      case '1day':
        return 'Last 24 Hours';
      case '7days':
        return 'Last 7 Days';
      case '30days':
        return 'Last 30 Days';
      case '12months':
        return 'Last 12 Months';
      case 'custom':
        return `${customRange.startDate.toLocaleDateString()} - ${customRange.endDate.toLocaleDateString()}`;
      default:
        return 'Last 30 Days';
    }
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectPeriod = (newPeriod) => {
    onPeriodChange(newPeriod);
    setIsOpen(false);
  };

  const handleApplyCustomRange = () => {
    const startDate = new Date(tempStartDate);
    const endDate = new Date(tempEndDate);
    
    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      onCustomRangeChange(startDate, endDate);
      onPeriodChange('custom');
    }
    
    setIsOpen(false);
  };

  return (
    <SelectorContainer>
      <SelectorButton onClick={handleToggleDropdown}>
        <FaCalendarAlt />
        <span>{getPeriodLabel()}</span>
        <FaChevronDown />
      </SelectorButton>
      
      {isOpen && (
        <DropdownMenu>
          <MenuItem 
            className={period === '1day' ? 'active' : ''}
            onClick={() => handleSelectPeriod('1day')}
          >
            Last 24 Hours
          </MenuItem>
          <MenuItem 
            className={period === '7days' ? 'active' : ''}
            onClick={() => handleSelectPeriod('7days')}
          >
            Last 7 Days
          </MenuItem>
          <MenuItem 
            className={period === '30days' ? 'active' : ''}
            onClick={() => handleSelectPeriod('30days')}
          >
            Last 30 Days
          </MenuItem>
          <MenuItem 
            className={period === '12months' ? 'active' : ''}
            onClick={() => handleSelectPeriod('12months')}
          >
            Last 12 Months
          </MenuItem>
          
          <CustomRangeContainer>
            <InputGroup>
              <label>Start Date</label>
              <input 
                type="date" 
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </InputGroup>
            
            <InputGroup>
              <label>End Date</label>
              <input 
                type="date" 
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </InputGroup>
            
            <ApplyButton onClick={handleApplyCustomRange}>
              Apply Custom Range
            </ApplyButton>
          </CustomRangeContainer>
        </DropdownMenu>
      )}
    </SelectorContainer>
  );
};

export default DateRangeSelector;